/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
    }

    connectedCallback() {
      this.controller = new AbortController();
      this.init();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          if (url.includes('v=')) {
            this.videoId = url.split('v=').pop().split('&')[0];
          } else {
            this.videoId = url.split('?')[0].split('/').pop();
          }

          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        this.closest('.video-section').addEventListener('click', (event) => {
          // Only resume on video area clicks, not overlay content
          if (event.target.closest('.text-overlay')) return;
          this.pausedByUser = false;
          this.play();
        }, { signal: this.controller.signal });
      }
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err);  // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          // Bail if the component was torn down (e.g. a viewport swap) while the
          // API / YT.ready callback was still pending — avoids building a player
          // on a detached node.
          if (this.controller.signal.aborted) return;
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer.bind(this), { signal: this.controller.signal });
        this.loadScript('//www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        this.player = new Vimeo.Player(this, {
          id: this.videoId,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        // Bail if the component was torn down during the awaited ready() — the
        // node is detached and any further setup would be wasted / leak.
        if (this.controller.signal.aborted) return;
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.closest('.video-section').classList.add('video-section--played');
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer.bind(this), { signal: this.controller.signal });
        this.loadScript('//player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        // Clear user-pause flag whenever playback actually starts so the
        // observer can correctly auto-resume/pause on subsequent scroll.
        this.pausedByUser = false;
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      this.setupToggleButton();
      this.setupMuteButton();

      // `.video-section` is what gates the custom control bar's visibility
      // (assets/video.css holds it at opacity 0 until `video-section--played`
      // or `video-section--autoplay-blocked`).
      // It is optional here rather than assumed: a <video-component> mounted
      // without that ancestor — any future call site that forgets it — used to
      // throw a TypeError and take the whole controller down with it.
      const videoSection = this.closest('.video-section');

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        videoSection?.classList.add('video-section--played');
      } else {
        this.player.addEventListener('play', () => {
          if (!videoSection) return;

          // After a refused autoplay the poster is still up and nothing was
          // buffered before the tap. `play` fires before any frame decodes, so
          // swapping here would fade the poster onto an empty box while the
          // video loads — wait for the first `playing` instead. One listener
          // per wait: a pause and re-play before frames arrive reuses it.
          if (videoSection.classList.contains('video-section--autoplay-blocked')) {
            if (this.awaitingFirstFrame) return;
            this.awaitingFirstFrame = true;
            this.player.addEventListener('playing', () => {
              this.awaitingFirstFrame = false;
              videoSection.classList.remove('video-section--autoplay-blocked');
              videoSection.classList.add('video-section--played');
            }, { once: true, signal: this.controller.signal });
            return;
          }

          videoSection.classList.add('video-section--played');
        });

        this.addObserver(this.player);

        // Mute the video if a click didn't trigger its load
        const deferredMedia = this.closest('deferred-media');
        if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
          this.player.muted = true;
        }

        this.play();
      }
    }

    /**
     * Wires the custom play/pause toggle button (HTML5 video only) to the
     * underlying <video> element and keeps its aria state in sync.
     */
    setupToggleButton() {
      const section = this.closest('.video-section');
      const toggleBtn = section && section.querySelector('[data-video-toggle]');
      if (!toggleBtn || !this.player) return;

      const labelPlay = toggleBtn.dataset.labelPlay || '';
      const labelPause = toggleBtn.dataset.labelPause || '';

      const reflectPlayingState = (isPlaying) => {
        toggleBtn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        toggleBtn.setAttribute('aria-label', isPlaying ? labelPause : labelPlay);
      };

      toggleBtn.addEventListener('click', (event) => {
        // Background-video sections register a section-wide click → play()
        // listener; stop the bubble so pausing here doesn't immediately re-play.
        event.stopPropagation();
        if (this.player.paused || this.player.ended) {
          this.player.play();
        } else {
          this.player.pause();
        }
      }, { signal: this.controller.signal });

      this.player.addEventListener('play', () => reflectPlayingState(true), { signal: this.controller.signal });
      this.player.addEventListener('pause', () => reflectPlayingState(false), { signal: this.controller.signal });
      this.player.addEventListener('ended', () => reflectPlayingState(false), { signal: this.controller.signal });

      // Initial sync — covers the case where the video is already playing
      // by the time we wire up.
      reflectPlayingState(!this.player.paused && !this.player.ended);
    }

    /**
     * Wires the custom mute/unmute toggle button (HTML5 video only) to the
     * underlying <video> element and keeps its aria state in sync.
     */
    setupMuteButton() {
      const section = this.closest('.video-section');
      const muteBtn = section && section.querySelector('[data-video-mute-toggle]');
      if (!muteBtn || !this.player) return;

      const labelMute = muteBtn.dataset.labelMute || '';
      const labelUnmute = muteBtn.dataset.labelUnmute || '';

      const reflectMutedState = (isMuted) => {
        muteBtn.setAttribute('aria-pressed', isMuted ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', isMuted ? labelUnmute : labelMute);
      };

      // Toggle synchronously inside the click handler so iOS preserves the
      // user-gesture chain required to unmute autoplay-muted video. Also stop
      // propagation so the background-video section-wide click → play() listener
      // doesn't fire on a click meant for the mute control.
      muteBtn.addEventListener('click', (event) => {
        event.stopPropagation();
        this.player.muted = !this.player.muted;
      }, { signal: this.controller.signal });

      this.player.addEventListener('volumechange', () => reflectMutedState(this.player.muted), { signal: this.controller.signal });

      // Initial sync — the video may have been force-muted by initVideoTag.
      reflectMutedState(this.player.muted);
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      this.setupIframe('js-youtube');

      if (this.autoplay) this.player.mute();
      this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.closest('.video-section').classList.add('video-section--played');
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Guard: callers reach here via async API callbacks, so the component may
      // already be detached (viewport swap). Don't size/observe a dead node.
      if (this.controller.signal.aborted || !this.querySelector('iframe')) return;

      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', window.debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200), { signal: this.controller.signal });
      }

      this.iframe = this.querySelector('iframe');
      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video when scrolled into view (if not paused by the user).
     * A refused play is recovered from rather than swallowed — see below.
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        this.player.playVideo();
      } else {
        // A refused autoplay (iOS Low Power Mode, an iPhone running hot —
        // thermal mitigation — data saver, a browser-level autoplay block)
        // rejects this promise and fires no `play` event, so
        // `video-section--played` never lands and assets/video.css keeps the
        // custom control bar at `opacity: 0; pointer-events: none`. The
        // autoplay markup renders no poster play button either, so the visitor
        // would be left with nothing to click. Revealing the bar is the
        // recovery: it carries play/pause and mute.
        //
        // HTML5 <video> gets its own state for that, not `--played`: iOS paints
        // no frame for a video that never played, so fading the poster on
        // refusal (what `--played` does) left an empty box.
        // `video-section--autoplay-blocked` reveals the bar and keeps the
        // poster; the `play` listener in initVideoTag() swaps it for
        // `--played` once frames are on screen. An `AbortError` is not a
        // refusal — the viewport observer pausing a pending play() raises it —
        // and a video that already played has its frame painted, so neither
        // needs recovering.
        //
        // Vimeo keeps the `--played` fallback: the iframe gets no ILVA bar and
        // a tap on the section cannot gesture-start it, so a pinned poster
        // would leave no way to play.
        //
        // Both HTML5 <video> and the Vimeo SDK return a promise here; YouTube
        // is handled above and returns nothing, hence the thenable guard.
        const playback = this.player.play();
        if (playback && typeof playback.catch === 'function') {
          playback.catch((error) => {
            const section = this.closest('.video-section');
            if (!section) return;

            if (this.type !== 'tag') {
              section.classList.add('video-section--played');
            } else if (error?.name !== 'AbortError' && !section.classList.contains('video-section--played')) {
              section.classList.add('video-section--autoplay-blocked');
            }
          });
        }
      }
    }

    /**
     * Pauses the video when scrolled out of view (if playing).
     */
    pause() {
      if (!this.player) return;

      if (this.type === 'youtube') {
        if (this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to pause the video when not in the viewport.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;

      if (!this.observer) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.inViewport = true;
              this.play();
            } else {
              this.inViewport = false;
              this.pause();
            }
          });
        }, { rootMargin: '0px 0px 200px 0px' });
      }

      this.observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
